"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RetailSalesOrdersModule = void 0;
const inversify_1 = require("inversify");
const http_status_codes_1 = require("http-status-codes");
const SalesOrder_1 = require("../../models/SalesOrder");
const addParamsToUrl_1 = require("../../../../../helpers/addParamsToUrl");
const createPayloadObjects_1 = require("./helpers/createPayloadObjects");
const errorTypes_1 = require("../errorTypes");
const SalesOrderPaymentIntent_1 = require("../../models/SalesOrderPaymentIntent");
let RetailSalesOrdersModule = class RetailSalesOrdersModule {
    /**
     * Setup injected dependencies.
     */
    constructor(_httpConnection) {
        this._httpConnection = _httpConnection;
        this._baseUrl = "salesOrders";
    }
    /**
     * Retrieve details of a particular sales order
     */
    async retrieveSalesOrderById(salesOrderId) {
        const res = await this._httpConnection.makeRequest({
            url: `${this._baseUrl}/${salesOrderId}`,
            method: "get"
        });
        const salesOrder = SalesOrder_1.SalesOrder.unserialize(res.salesOrder);
        return salesOrder;
    }
    /**
     * Validate the sales order and create a payment intent before confirming it
     */
    async validateSalesOrder(salesOrder, options) {
        try {
            const res = await this._httpConnection.makeRequest({
                url: `${this._baseUrl}/validateOrder`,
                method: "post",
                data: {
                    salesOrder: (0, createPayloadObjects_1.createSalesOrderPayloadObject)(salesOrder),
                    options: options
                },
                handleOwnError: true
            });
            return SalesOrderPaymentIntent_1.SalesOrderPaymentIntent.unserialize(res);
        }
        catch (e) {
            if (e.response.status === http_status_codes_1.StatusCodes.BAD_REQUEST) {
                switch (e.response.data.message) {
                    case "error completing sales order in erp":
                        // handle special case where payment has been taken but the sales order hasn't been completed
                        throw new errorTypes_1.CompleteSalesOrderError(e.response.data.newTemporarySalesOrderId);
                        break;
                    case "error completing payment":
                        throw new errorTypes_1.SalesOrderPaymentError(`${e.response.data.newTemporarySalesOrderId}-${e.response.data.description}`);
                        break;
                    case "delivery order unavailable":
                        throw new errorTypes_1.DeliveryServiceUnavailableError(e.response.data.description);
                        break;
                    case "out of hours order error":
                        throw new errorTypes_1.OutOfHoursOrderError(e.response.data.description);
                        break;
                    default:
                        throw new Error(e.response.data.error);
                }
            }
            throw e;
        }
    }
    /**
     * Really just a way for a retail customer to checkout their sales order
     */
    async createSalesOrder(salesOrder) {
        try {
            const res = await this._httpConnection.makeRequest({
                url: `${this._baseUrl}`,
                method: "post",
                data: {
                    salesOrder: (0, createPayloadObjects_1.createSalesOrderPayloadObject)(salesOrder)
                },
                handleOwnError: true
            });
            const newSalesOrder = SalesOrder_1.SalesOrder.unserialize(res.salesOrder);
            return newSalesOrder;
        }
        catch (e) {
            if (e.response.status === http_status_codes_1.StatusCodes.BAD_REQUEST) {
                switch (e.response.data.message) {
                    case "error completing sales order in erp":
                        // handle special case where payment has been taken but the sales order hasn't been completed
                        throw new errorTypes_1.CompleteSalesOrderError(e.response.data.newTemporarySalesOrderId);
                        break;
                    case "error completing payment":
                        throw new errorTypes_1.SalesOrderPaymentError(`${e.response.data.newTemporarySalesOrderId}-${e.response.data.description}`);
                        break;
                    case "delivery order unavailable":
                        throw new errorTypes_1.DeliveryServiceUnavailableError(e.response.data.description);
                        break;
                    case "out of hours order error":
                        throw new errorTypes_1.OutOfHoursOrderError(e.response.data.description);
                        break;
                    default:
                        throw new Error(e.response.data.error);
                }
            }
            if (e.response.status === http_status_codes_1.StatusCodes.FORBIDDEN) {
                throw new errorTypes_1.PaymentExceededDenied("Your account has been suspended due to suspicious attempts. Further details have been sent to your email address.");
            }
            if (e.response.status === http_status_codes_1.StatusCodes.CONFLICT) {
                throw new errorTypes_1.DuplicateDeliveryError("Duplicate delivery order ID received. Please restart a new order");
            }
            throw e;
        }
    }
    /**
     * Update a sales order to the given status
     * This will also send updates to the retail customer and retail location
     */
    async updateSalesOrder(salesOrderId, status) {
        await this._httpConnection.makeRequest({
            url: `${this._baseUrl}/${salesOrderId}/set-status`,
            method: "post",
            data: {
                status
            }
        });
    }
    /**
     * Refund a customer's sales order
     */
    async refundSalesOrder(salesOrder) {
        await this._httpConnection.makeRequest({
            url: `${this._baseUrl}/${salesOrder.salesOrderId}/refund`,
            method: "post",
            data: {
                salesOrderRefund: (0, createPayloadObjects_1.createSalesOrderRefundPayloadObject)(salesOrder.salesOrderRefund)
            }
        });
    }
    /**
     * Retrieve a temporary sales order.
     * This is used for populating the cart.
     */
    async retrieveTemporarySalesOrder(salesOrderId, orderType) {
        const res = await this._httpConnection.makeRequest({
            url: `temporarySalesOrders/${salesOrderId}?orderType=${orderType}`,
            method: "get"
        });
        const salesOrder = SalesOrder_1.SalesOrder.unserialize(res.salesOrder);
        return salesOrder;
    }
    /**
     * Update a temporary sales order.
     * This is used for updating the cart with the updated temporary
     * sales order that has any applicable promotion code attached
     * and total price re-calculated from the backend.
     *
     * If the temporary sales order does not have a salesOrderId
     * (this is the case when the customer adds the first item to
     * an empty cart) or a temporary sales order with the provided
     * salesOrderId cannot be found for some reason, the backend
     * will create a new temporary sales order and return it .
     */
    async setTemporarySalesOrder(salesOrder) {
        const res = await this._httpConnection.makeRequest({
            url: "temporarySalesOrders",
            method: "post",
            data: {
                salesOrder: (0, createPayloadObjects_1.createSalesOrderPayloadObject)(salesOrder)
            }
        });
        const updatedSalesOrder = SalesOrder_1.SalesOrder.unserialize(res.salesOrder);
        return updatedSalesOrder;
    }
    /**
     * Delete a temporary sales order.
     */
    async deleteTemporarySalesOrder(salesOrderId) {
        await this._httpConnection.makeRequest({
            url: `temporarySalesOrders/${salesOrderId}`,
            method: "delete"
        });
    }
    /**
     * Retrieve sales orders for a given retail location to reprint
     */
    retrieveSyncedRetailLocationSalesOrders(retailLocationId, type, params) {
        return {
            endpoint: `${this._baseUrl}`,
            action: "retrieveSyncedRetailLocationSalesOrders",
            parameters: {
                retailLocationId,
                type,
                params
            },
            unseralizeData: (data) => data.salesOrders.map((salesOrder) => SalesOrder_1.SalesOrder.unserialize(salesOrder))
        };
    }
    /**
     * Retrieve retail customer's by given type
     * Currently - ["Outstanding"]
     */
    retrieveSyncedRetailCustomerSalesOrders(retailCustomerId, type, params) {
        return {
            endpoint: `${this._baseUrl}`,
            action: "retrieveSyncedRetailCustomerSalesOrders",
            parameters: {
                retailCustomerId,
                type,
                params
            },
            unseralizeData: (data) => data.salesOrders.map((salesOrder) => SalesOrder_1.SalesOrder.unserialize(salesOrder))
        };
    }
    /**
     * Retrieve retail customer's sales order for type and given params
     */
    async retrieveRetailCustomerSalesOrders(retailCustomerId, type, params) {
        const url = `${this._baseUrl}?retailCustomerId=${retailCustomerId}&type=${type}&`;
        const urlWithExtraParams = (0, addParamsToUrl_1.addParamsToUrlString)(url, params);
        const res = await this._httpConnection.makeRequest({
            url: urlWithExtraParams,
            method: "get"
        });
        const salesOrders = res.salesOrders.map((salesOrder) => SalesOrder_1.SalesOrder.unserialize(salesOrder));
        return salesOrders;
    }
    /**
     * Update a sales order to the given status
     * This will also send updates to the retail customer and retail location
     */
    async updateSalesOrderPrintStatus(salesOrderId, printInfo) {
        await this._httpConnection.makeRequest({
            url: `${this._baseUrl}/${salesOrderId}/update-print-status`,
            method: "post",
            data: {
                printInfo
            }
        });
    }
    /**
     * Resend a sales order invoice for a customer
     *
     * This can only be done for completed orders as this action will be
     * performed from the past orders page
     */
    async sendSalesOrderInvoice(salesOrderId) {
        await this._httpConnection.makeRequest({
            url: `${this._baseUrl}/${salesOrderId}/email-invoice`,
            method: "post"
        });
    }
    async cancelSalesOrder(salesOrderId) {
        await this._httpConnection.makeRequest({
            url: `${this._baseUrl}/${salesOrderId}/cancel`,
            method: "post"
        });
    }
};
RetailSalesOrdersModule = __decorate([
    (0, inversify_1.injectable)(),
    __param(0, (0, inversify_1.inject)("HttpConnection"))
], RetailSalesOrdersModule);
exports.RetailSalesOrdersModule = RetailSalesOrdersModule;
//# sourceMappingURL=RetailSalesOrdersModule.js.map